import * as React from "react";
import { Mic, Square } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";

interface DictationResultItem {
  0: { transcript: string };
  isFinal: boolean;
  length: number;
}

interface DictationResultList {
  item(index: number): DictationResultItem;
  length: number;
}

interface DictationEvent {
  results: DictationResultList;
}

interface DictationErrorEvent {
  error: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: DictationEvent) => void) | null;
  onerror: ((event: DictationErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionFactory {
  new (): SpeechRecognitionLike;
}

interface DictationWindow extends Window {
  SpeechRecognition?: SpeechRecognitionFactory;
  webkitSpeechRecognition?: SpeechRecognitionFactory;
}

interface VoiceButtonProps {
  targetRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  language?: string;
  autoRedact?: boolean;
  disabled?: boolean;
  className?: string;
}

function setNativeValue(node: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto =
    node instanceof HTMLTextAreaElement
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
  descriptor?.set?.call(node, value);
  node.dispatchEvent(new Event("input", { bubbles: true }));
}

function appendWithSeparator(current: string, appended: string): string {
  const trimmed = appended.trim();
  if (!trimmed) return current;
  if (!current.trim()) return trimmed;
  return `${current.trimEnd()} ${trimmed}`;
}

export const VoiceButton = React.memo<VoiceButtonProps>(
  ({ targetRef, language = "pl-PL", autoRedact = true, disabled = false, className }) => {
    const recognitionRef = React.useRef<SpeechRecognitionLike | null>(null);
    const [isListening, setIsListening] = React.useState(false);
    const [isRedacting, setIsRedacting] = React.useState(false);
    const [supported, setSupported] = React.useState(false);

    React.useEffect(() => {
      const win = window as DictationWindow;
      setSupported(Boolean(win.SpeechRecognition || win.webkitSpeechRecognition));
      return () => {
        recognitionRef.current?.stop();
        recognitionRef.current = null;
      };
    }, []);

    const redactTranscript = React.useCallback(
      async (transcript: string): Promise<string> => {
        if (!autoRedact) return transcript;
        try {
          setIsRedacting(true);
          const response = await apiRequest<{ text?: string }>("POST", "/api/ai/redact-dictation", {
            text: transcript,
          });
          if (typeof response?.text !== "string") return transcript;
          return response.text.trim() || transcript;
        } catch {
          return transcript;
        } finally {
          setIsRedacting(false);
        }
      },
      [autoRedact]
    );

    const stopListening = React.useCallback(() => {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
      setIsListening(false);
    }, []);

    const startListening = React.useCallback(() => {
      const node = targetRef.current;
      if (!node) return;

      const win = window as DictationWindow;
      const Factory = win.SpeechRecognition || win.webkitSpeechRecognition;
      if (!Factory) return;

      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }

      const recognition = new Factory();
      recognition.lang = language;
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onerror = () => {
        setIsListening(false);
        recognitionRef.current = null;
      };

      recognition.onresult = async (event: DictationEvent) => {
        const transcript = event.results.item(0)?.[0]?.transcript?.trim() ?? "";
        if (!transcript) return;
        const redacted = await redactTranscript(transcript);
        const next = appendWithSeparator(node.value, redacted);
        setNativeValue(node, next);
      };

      recognition.onend = () => {
        setIsListening(false);
        recognitionRef.current = null;
      };

      recognitionRef.current = recognition;
      setIsListening(true);
      recognition.start();
    }, [targetRef, language, redactTranscript]);

    if (!supported) return null;

    const isBusy = isListening || isRedacting;

    return (
      <button
        type="button"
        onClick={isListening ? stopListening : startListening}
        disabled={disabled || isRedacting}
        title={isListening ? "Zatrzymaj dyktowanie" : "Dyktuj i redaguj AI"}
        aria-label={isListening ? "Zatrzymaj dyktowanie" : "Dyktuj i redaguj AI"}
        className={cn(
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/20 bg-black/60 text-white transition-colors",
          isListening ? "border-red-400/50 text-red-300" : "hover:bg-black/80",
          isBusy ? "cursor-wait" : "",
          className
        )}
      >
        {isListening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </button>
    );
  }
);

VoiceButton.displayName = "VoiceButton";
